import { useCallback, useRef } from 'react'
import { SIGNUP_STORAGE_KEY } from '../constants'
import { type SignUpFormData, DEFAULT_FORM_DATA } from '../types'

const readFromStorage = (): SignUpFormData => {
	try {
		const raw = sessionStorage.getItem(SIGNUP_STORAGE_KEY)
		if (!raw) return DEFAULT_FORM_DATA
		return { ...DEFAULT_FORM_DATA, ...JSON.parse(raw) }
	} catch {
		return DEFAULT_FORM_DATA
	}
}

const writeToStorage = (data: SignUpFormData): void => {
	try {
		sessionStorage.setItem(SIGNUP_STORAGE_KEY, JSON.stringify(data))
	} catch {
		// sessionStorage full or unavailable
	}
}

const clearStorage = (): void => {
	try {
		sessionStorage.removeItem(SIGNUP_STORAGE_KEY)
	} catch {
		// ignore
	}
}

/**
 * Provides read / write / clear helpers for the signup wizard data in sessionStorage.
 * Uses a ref-based approach so callers always get the latest snapshot
 * without causing re-renders on every persist.
 */
export const useSignupPersistence = () => {
	const dataRef = useRef<SignUpFormData>(readFromStorage())

	const load = useCallback((): SignUpFormData => {
		dataRef.current = readFromStorage()
		return dataRef.current
	}, [])

	const save = useCallback((patch: Partial<SignUpFormData>): SignUpFormData => {
		const next = { ...dataRef.current, ...patch }
		dataRef.current = next
		writeToStorage(next)
		return next
	}, [])

	const clear = useCallback((): void => {
		dataRef.current = DEFAULT_FORM_DATA
		clearStorage()
	}, [])

	return { load, save, clear } as const
}
